import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Star, QrCode, Copy, CheckCircle, Plus, Trash, DownloadSimple,
  Check, X, ChatCircle,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail, API } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const Stars = ({ value, size = 14 }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} weight={n <= value ? "fill" : "regular"} size={size} className={n <= value ? "text-amber-400" : "text-slate-300"} />
    ))}
  </div>
);

const STATUS_BADGE = {
  published: "bg-emerald-100 text-emerald-700 border-emerald-200",
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
};

export default function ReviewsManager() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [links, setLinks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(false);
  const [respondId, setRespondId] = useState(null);
  const [respondText, setRespondText] = useState("");

  const publicUrl = data ? `${window.location.origin}/r/${data.review_slug}` : "";

  const load = async () => {
    try {
      const { data: d } = await api.get(`/businesses/${businessId}/reviews`);
      setData(d);
      setLinks(d.review_links || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (err.response?.status === 404) navigate("/dashboard");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [businessId]);

  const saveLinks = async () => {
    try {
      await api.put(`/businesses/${businessId}/review-settings`, { review_links: links.filter((l) => l.url.trim()) });
      toast.success("Review links saved");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const moderate = async (reviewId, changes) => {
    try {
      await api.patch(`/businesses/${businessId}/reviews/${reviewId}`, changes);
      toast.success("Review updated");
      setRespondId(null); setRespondText("");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true); toast.success("Review link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  if (!data) return <Layout><div className="max-w-6xl mx-auto px-6 py-10"><div className="h-40 rounded-xl bg-slate-50 border border-slate-200 animate-pulse" /></div></Layout>;

  const s = data.stats;
  const reviews = data.reviews.filter((r) => filter === "all" || r.status === filter);
  const qrUrl = `${API}/businesses/${businessId}/review-qr?origin=${encodeURIComponent(window.location.origin)}`;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(`/business/${businessId}`)} data-testid="reviews-back-button"
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
          <ArrowLeft size={16} /> Back to business
        </button>

        <div className="mt-6 flex items-center gap-2 text-[#0055FF]"><Star weight="fill" size={20} /><p className="text-xs font-bold uppercase tracking-[0.14em]">Reviews</p></div>
        <h1 className="font-display text-4xl font-black tracking-tighter text-slate-900 mt-2">Collect & manage reviews</h1>

        <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg">
          <Stat label="Average" value={s.average || "—"} sub={<Stars value={Math.round(s.average)} size={12} />} />
          <Stat label="Published" value={s.published} accent="text-emerald-600" />
          <Stat label="Awaiting approval" value={s.pending} accent="text-amber-600" />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-8">
          {/* QR + link */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 text-slate-700"><QrCode size={18} /><p className="text-sm font-bold uppercase tracking-[0.1em]">Share your review page</p></div>
            <div className="mt-4 flex gap-5 items-center">
              <img src={qrUrl} alt="Review QR code" data-testid="review-qr-img" className="h-36 w-36 rounded-lg border border-slate-200" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-600">Print the QR on receipts or flyers, or share the link by text. It sends customers straight to your review page.</p>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" onClick={copyLink} data-testid="copy-review-link" className="bg-[#0055FF] hover:bg-[#0044CC] text-white">
                    {copied ? <CheckCircle weight="fill" size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />}{copied ? "Copied" : "Copy link"}
                  </Button>
                  <a href={qrUrl} download={`${data.review_slug}-qr.png`}>
                    <Button size="sm" variant="secondary" data-testid="download-qr" className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"><DownloadSimple size={15} className="mr-1.5" /> QR</Button>
                  </a>
                </div>
                <p className="mt-2 text-xs text-slate-400 font-mono truncate">{publicUrl}</p>
              </div>
            </div>
          </div>

          {/* Directory review links */}
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <p className="text-sm font-bold uppercase tracking-[0.1em] text-slate-700">Your public review pages</p>
            <p className="text-xs text-slate-500 mt-1">Add your Google / Yelp / Facebook review-page links so customers can easily leave a review on the platforms that matter to you.</p>
            <div className="mt-4 space-y-2">
              {links.map((l, i) => (
                <div key={i} className="flex gap-2" data-testid={`review-link-row-${i}`}>
                  <Input value={l.label} onChange={(e) => setLinks((prev) => prev.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} placeholder="Google" className="w-32 h-9" data-testid={`review-link-label-${i}`} />
                  <Input value={l.url} onChange={(e) => setLinks((prev) => prev.map((x, j) => j === i ? { ...x, url: e.target.value } : x))} placeholder="https://g.page/…/review" className="flex-1 h-9" data-testid={`review-link-url-${i}`} />
                  <button onClick={() => setLinks((prev) => prev.filter((_, j) => j !== i))} data-testid={`remove-link-${i}`} className="h-9 w-9 rounded-md flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash size={16} /></button>
                </div>
              ))}
              <div className="flex gap-2 pt-1">
                <Button size="sm" variant="secondary" onClick={() => setLinks((prev) => [...prev, { label: "", url: "" }])} data-testid="add-review-link" className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"><Plus size={15} className="mr-1.5" /> Add link</Button>
                <Button size="sm" onClick={saveLinks} data-testid="save-review-links" className="bg-[#0055FF] hover:bg-[#0044CC] text-white">Save</Button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews list */}
        <div className="mt-8 flex items-center gap-2">
          {["all", "pending", "published", "rejected"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold capitalize transition-colors ${filter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {f === "pending" ? "Awaiting approval" : f}
            </button>
          ))}
        </div>

        <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
          {reviews.length === 0 ? (
            <div className="py-16 text-center text-slate-500">No reviews here yet.</div>
          ) : reviews.map((r) => (
            <div key={r.review_id} data-testid={`review-row-${r.review_id}`} className="px-6 py-4 border-b border-slate-100 last:border-0">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2.5">
                    <span className="font-semibold text-slate-900">{r.reviewer_name}</span>
                    <Stars value={r.rating} size={13} />
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold capitalize ${STATUS_BADGE[r.status]}`}>
                      {r.status === "pending" ? "Awaiting approval" : r.status}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-slate-600 mt-1.5">{r.comment}</p>}
                  {r.response && <p className="text-sm text-slate-700 mt-2 pl-3 border-l-2 border-[#0055FF]"><span className="font-semibold">Your reply: </span>{r.response}</p>}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {r.status === "pending" && (
                    <>
                      <Button size="sm" onClick={() => moderate(r.review_id, { status: "published" })} data-testid={`approve-${r.review_id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8"><Check size={14} className="mr-1" /> Approve</Button>
                      <Button size="sm" variant="secondary" onClick={() => moderate(r.review_id, { status: "rejected" })} data-testid={`reject-${r.review_id}`} className="bg-white border border-slate-200 text-red-600 hover:bg-red-50 h-8"><X size={14} className="mr-1" /> Reject</Button>
                    </>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => { setRespondId(respondId === r.review_id ? null : r.review_id); setRespondText(r.response || ""); }} data-testid={`respond-${r.review_id}`} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-8"><ChatCircle size={14} className="mr-1" /> Reply</Button>
                </div>
              </div>
              {respondId === r.review_id && (
                <div className="mt-3 flex gap-2">
                  <Textarea value={respondText} onChange={(e) => setRespondText(e.target.value)} rows={2} placeholder="Write a public response…" data-testid={`respond-input-${r.review_id}`} />
                  <Button onClick={() => moderate(r.review_id, { response: respondText })} data-testid={`save-response-${r.review_id}`} className="bg-[#0055FF] hover:bg-[#0044CC] text-white self-start">Save</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}

const Stat = ({ label, value, sub, accent = "text-slate-900" }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <p className="text-xs font-bold uppercase tracking-[0.1em] text-slate-400">{label}</p>
    <p className={`font-display text-3xl font-black mt-1 ${accent}`}>{value}</p>
    {sub && <div className="mt-1">{sub}</div>}
  </div>
);
